/**
 * Сервис аутентификации
 * Вход в админку по одноразовому коду TOTP (Google Authenticator) и работа с JWT
 */

import api from './api'
import type {
  User,
  TotpVerifyResult,
  TotpEnrollResult,
} from '@/types'

class AuthService {
  /**
   * Проверка одноразового кода TOTP.
   * Код берётся из приложения-аутентификатора и меняется каждые 30 секунд.
   * При успехе сервер выдаёт JWT владельца.
   */
  async verifyTotp(code: string): Promise<TotpVerifyResult> {
    const response = await api.post<TotpVerifyResult>('/auth/totp/verify', { code })
    return response.data
  }

  /**
   * Первичная настройка TOTP: сервер отдаёт секрет, otpauth-ссылку и QR-код.
   * Доступно только пока секрет не задан (иначе сервер вернёт 409).
   */
  async enrollTotp(): Promise<TotpEnrollResult> {
    const response = await api.post<TotpEnrollResult>('/auth/totp/enroll', {})
    return response.data
  }

  /**
   * Получение текущего пользователя.
   * `/auth/me` отдаёт плоский объект (без обёртки data), поэтому возвращаем его как есть.
   */
  async getCurrentUser(): Promise<TotpVerifyResult['user']> {
    const response = await api.get<TotpVerifyResult['user']>('/auth/me')
    return response.data
  }

  /**
   * Обновление профиля пользователя.
   * На бэкенде НЕТ эндпоинта PATCH /auth/profile (AuthController его не
   * реализует) — выдумывать путь нечестно. Бросаем понятную ошибку вместо
   * тихого запроса в никуда; вызывающий стор (stores/auth.ts.updateProfile)
   * уже оборачивает вызов в try/catch и покажет error.value пользователю.
   */
  async updateProfile(_data: Partial<User>): Promise<User> {
    throw new Error('Обновление профиля не поддерживается бэкендом (эндпоинт /auth/profile отсутствует)')
  }

  /**
   * Загрузка аватара пользователя.
   * На бэкенде НЕТ эндпоинта POST /auth/avatar — см. комментарий выше про
   * updateProfile, та же ситуация.
   */
  async uploadAvatar(_file: File): Promise<{ avatarUrl: string }> {
    throw new Error('Загрузка аватара не поддерживается бэкендом (эндпоинт /auth/avatar отсутствует)')
  }
}

export const authService = new AuthService()
